import React, { useRef, useCallback, useMemo } from "react";
import { Download } from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  BarChart,
  PieChart,
  AreaChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Line,
  Bar,
  Pie,
  Cell,
  Area,
  ComposedChart,
  Scatter,
  ScatterChart,
  ZAxis,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ReferenceLine,
} from "recharts";

export interface ChartDataPoint {
  [key: string]: string | number | null;
}

export type ChartType =
  | "line"
  | "bar"
  | "monochrome-bar"
  | "pie"
  | "donut"
  | "area"
  | "stacked-area"
  | "scatter"
  | "bubble"
  | "radar"
  | "mixed"
  | "implicit";

export interface ChartSeries {
  key: string;
  name?: string;
  type?: "line" | "area" | "bar";
  color?: string;
  strokeWidth?: number;
  yAxisId?: string;
}

export interface ChartConfig {
  type: ChartType;
  title?: string;
  description?: string;
  xKey: string;
  yKeys: ChartSeries[];
  data: ChartDataPoint[];
  colors?: string[];
  valueFormatter?: (value: number) => string;
  categoryKey?: string;
  unit?: string;
  showLegend?: boolean;
  showTooltip?: boolean;
  stacked?: boolean;
  area?: boolean;
  scatterSizeKey?: string;
  radarAngleKey?: string;
  radarRadiusKey?: string;
  mixedConfig?: ChartSeries[];
  cartesianPlane?: boolean;
  showZeroAxes?: boolean;
  xLabel?: string;
  yLabel?: string;
  /** Implicit plotting options (only used when type === 'implicit'). */
  implicitEquation?: string; // e.g. "(x^2 + y^2 - 1)^3 - x^2*y^3"
  implicitFunction?: (x: number, y: number) => number; // f(x,y)=0
  xRange?: [number, number];
  yRange?: [number, number];
  resolution?: number; // grid cells per axis (default 200)
}

const COLOR_PALETTE = [
  "#6366F1",
  "#14B8A6",
  "#F97316",
  "#EC4899",
  "#FACC15",
  "#0EA5E9",
  "#8B5CF6",
  "#22C55E",
  "#F43F5E",
  "#34D399",
];

const defaultValueFormatter = (value: number) => {
  if (typeof value !== "number" || Number.isNaN(value)) return "";
  if (Math.abs(value) >= 1_000_000_000)
    return `${(value / 1_000_000_000).toFixed(1)}B`;
  if (Math.abs(value) >= 1_000_000)
    return `${(value / 1_000_000).toFixed(1)}M`;
  if (Math.abs(value) >= 1_000)
    return `${(value / 1_000).toFixed(1)}K`;
  if (Math.abs(value) >= 1)
    return value.toFixed(2);
  return value.toPrecision(2);
};

const pickColor = (index: number, colors?: string[]) => {
  if (colors && colors[index]) return colors[index];
  return COLOR_PALETTE[index % COLOR_PALETTE.length];
};

// --- Implicit equation helpers ---
// Convert equation string like "(x^2 + y^2 - 1)^3 = x^2*y^3" to f(x,y) = 0 form string
const toImplicitExpr = (equation?: string): string | null => {
  if (!equation || typeof equation !== "string") return null;
  // Replace unicode superscripts if present (², ³)
  let expr = equation
    .replace(/\u00B2/g, "^2")
    .replace(/\u00B3/g, "^3")
    .replace(/\s+/g, " ");
  // Ensure caret becomes JS exponent
  expr = expr.replace(/\^/g, "**");
  // Split on '=' to left - right
  const parts = expr.split("=");
  if (parts.length === 2) {
    return `(${parts[0]}) - (${parts[1]})`;
  }
  return expr; // already f(x,y)
};

// Build a JS function f(x,y) from string using Function with Math in scope
const buildImplicitFn = (equation?: string): ((x: number, y: number) => number) | null => {
  const expr = toImplicitExpr(equation);
  if (!expr) return null;
  try {
    // eslint-disable-next-line no-new-func
    const fn = new Function("x", "y", `with (Math) { return (${expr}); }`) as (x: number, y: number) => number;
    // Test once
    void fn(0, 0);
    return fn;
  } catch {
    return null;
  }
};

// Marching squares zero-contour generator: returns SVG path segments (as 'M x y L x y ...') in pixel coords
const marchingSquaresPath = (
  f: (x: number, y: number) => number,
  xMin: number,
  xMax: number,
  yMin: number,
  yMax: number,
  cells: number,
  width: number,
  height: number,
  pad: number
): string => {
  const nx = Math.max(4, cells);
  const ny = Math.max(4, cells);
  const dx = (xMax - xMin) / nx;
  const dy = (yMax - yMin) / ny;

  const worldToPx = (x: number, y: number) => {
    const px = pad + ((x - xMin) / (xMax - xMin)) * (width - 2 * pad);
    const py = height - pad - ((y - yMin) / (yMax - yMin)) * (height - 2 * pad);
    return { x: px, y: py };
  };

  const sample: number[][] = Array.from({ length: ny + 1 }, (_, j) =>
    Array.from({ length: nx + 1 }, (_, i) => f(xMin + i * dx, yMin + j * dy))
  );

  const interp = (v1: number, v2: number) => {
    const denom = v1 - v2;
    if (denom === 0) return 0.5;
    return v1 / (v1 - v2);
  };

  let d = "";
  for (let j = 0; j < ny; j++) {
    for (let i = 0; i < nx; i++) {
      const f00 = sample[j][i];
      const f10 = sample[j][i + 1];
      const f01 = sample[j + 1][i];
      const f11 = sample[j + 1][i + 1];
      const idx = (f00 > 0 ? 1 : 0) | (f10 > 0 ? 2 : 0) | (f11 > 0 ? 4 : 0) | (f01 > 0 ? 8 : 0);
      if (idx === 0 || idx === 15) continue; // no crossing

      const x0 = xMin + i * dx;
      const y0 = yMin + j * dy;

      // Edge interpolation points
      const points: Array<{ x: number; y: number }> = [];

      // Left edge (between f00 and f01)
      if ((idx & 1) !== (idx & 8)) {
        const t = interp(f00, f01);
        points.push({ x: x0, y: y0 + t * dy });
      }
      // Right edge (between f10 and f11)
      if ((idx & 2) !== (idx & 4)) {
        const t = interp(f10, f11);
        points.push({ x: x0 + dx, y: y0 + t * dy });
      }
      // Top edge (between f00 and f10)
      if ((idx & 1) !== (idx & 2)) {
        const t = interp(f00, f10);
        points.push({ x: x0 + t * dx, y: y0 });
      }
      // Bottom edge (between f01 and f11)
      if ((idx & 8) !== (idx & 4)) {
        const t = interp(f01, f11);
        points.push({ x: x0 + t * dx, y: y0 + dy });
      }

      // Draw segments between pairs
      for (let k = 0; k + 1 < points.length; k += 2) {
        const p1 = worldToPx(points[k].x, points[k].y);
        const p2 = worldToPx(points[k + 1].x, points[k + 1].y);
        d += `M ${p1.x.toFixed(2)} ${p1.y.toFixed(2)} L ${p2.x.toFixed(2)} ${p2.y.toFixed(2)} `;
      }
    }
  }

  return d.trim();
};

const renderLineSeries = (
  series: ChartSeries[],
  colors?: string[],
  area?: boolean,
  stacked?: boolean
) => {
  return series.map((s, index) => {
    const color = s.color || pickColor(index, colors);
    const key = `${s.key}-${index}`;

    if (area || s.type === "area") {
      return (
        <Area
          key={key}
          type="monotone"
          dataKey={s.key}
          name={s.name || s.key}
          fill={color}
          stroke={color}
          fillOpacity={0.35}
          strokeWidth={s.strokeWidth || 2}
          stackId={stacked ? "stack" : undefined}
          yAxisId={s.yAxisId}
          dot={{ r: 3 }}
          connectNulls
          isAnimationActive={false}
        />
      );
    }

    return (
      <Line
        key={key}
        type="monotone"
        dataKey={s.key}
        name={s.name || s.key}
        stroke={color}
        strokeWidth={s.strokeWidth || 2}
        dot={{ r: 3 }}
        activeDot={{ r: 6 }}
        yAxisId={s.yAxisId}
        connectNulls
        isAnimationActive={false}
      />
    );
  });
};

const renderBarSeries = (
  series: ChartSeries[],
  colors?: string[],
  stacked?: boolean
) => {
  return series.map((s, index) => {
    const color = s.color || pickColor(index, colors);
    const key = `${s.key}-${index}`;

    return (
      <Bar
        key={key}
        dataKey={s.key}
        name={s.name || s.key}
        fill={color}
        radius={[6, 6, 0, 0]}
        stackId={stacked ? "stack" : undefined}
        yAxisId={s.yAxisId}
        isAnimationActive={false}
      />
    );
  });
};

const renderPieSeries = (
  config: ChartConfig,
  innerRadius?: string | number,
  outerRadius?: string | number
) => {
  const firstSeries = config.yKeys[0];
  return (
    <Pie
      data={config.data}
      dataKey={firstSeries.key}
      nameKey={config.categoryKey || config.xKey}
      innerRadius={innerRadius}
      outerRadius={outerRadius}
      paddingAngle={3}
      labelLine={false}
      label={({ name, value }) => `${name}: ${config.valueFormatter?.(value) || defaultValueFormatter(value)}`}
      isAnimationActive={false}
    >
      {config.data.map((entry, index) => {
        const color = pickColor(index, config.colors);
        return <Cell key={`cell-${index}`} fill={color} />;
      })}
    </Pie>
  );
};

const ChartRendererComp: React.FC<{ config: ChartConfig; isDarkMode?: boolean }> = ({
  config,
  isDarkMode = false,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const formatter = useMemo(() => config.valueFormatter || defaultValueFormatter, [config.valueFormatter]);
  const axisColor = useMemo(() => (isDarkMode ? "#9ca3af" : "#374151"), [isDarkMode]);

  // Compute axis labels with sensible defaults
  const xAxisLabel = useMemo(() => config.xLabel ?? config.xKey, [config.xLabel, config.xKey]);
  const yAxisLabel = useMemo(
    () =>
      config.yLabel ??
      (config.yKeys?.length === 1
        ? (config.yKeys[0].name || config.yKeys[0].key)
        : (config.unit ? `Value (${config.unit})` : "Value")),
    [config.yLabel, config.yKeys, config.unit]
  );

  // Memoized common objects to avoid re-creating inline objects that trigger re-renders
  // Balanced margins: minimize empty left space while keeping room for labels
  const commonMargins = useMemo(() => ({ top: 10, right: 24, bottom: 4, left: 4 }), []);
  const gridStrokeLight = "#e5e7eb";
  const gridStrokeDark = "#1f2937";
  const gridStrokeDarker = "#1e1e1e";
  const tooltipContentStyle = useMemo(
    () => ({ backgroundColor: isDarkMode ? "#111827" : "#ffffff", borderRadius: 12, borderColor: "transparent" }),
    [isDarkMode]
  );
  const tooltipLabelStyle = useMemo(() => ({ color: axisColor }), [axisColor]);

  const handleShare = useCallback(async () => {
    try {
      const container = containerRef.current;
      if (!container) return;
      // Select the Recharts chart SVG specifically, not the share icon SVG
      const svg =
        (container.querySelector('svg.recharts-surface') as SVGSVGElement | null) ||
        (container.querySelector('.recharts-wrapper svg') as SVGSVGElement | null);
      if (!svg) return;

      // Wait for fonts (helps text rendering in the export)
      try {
        const fonts = (document as any).fonts as FontFaceSet | undefined;
        if (fonts?.ready) {
          await fonts.ready;
        }
      } catch {}

      // Determine export bounds (avoid clipping labels, pie slices, etc.)
      const viewBox = svg.viewBox?.baseVal;
      const fallbackRect = svg.getBoundingClientRect();
      const fallbackWidth = Math.max(1, Math.round(fallbackRect.width || svg.clientWidth || 800));
      const fallbackHeight = Math.max(1, Math.round(fallbackRect.height || svg.clientHeight || 400));

      let exportX = viewBox?.x ?? 0;
      let exportY = viewBox?.y ?? 0;
      let exportW = viewBox?.width && viewBox.width > 0 ? viewBox.width : fallbackWidth;
      let exportH = viewBox?.height && viewBox.height > 0 ? viewBox.height : fallbackHeight;

      try {
        const b = svg.getBBox();
        if (Number.isFinite(b.x) && Number.isFinite(b.y) && b.width > 0 && b.height > 0) {
          exportX = b.x;
          exportY = b.y;
          exportW = b.width;
          exportH = b.height;
        }
      } catch {}

      const padding = 24;
      exportX -= padding;
      exportY -= padding;
      exportW += padding * 2;
      exportH += padding * 2;

      // Build a safe SVG string with expanded viewBox
      const clonedSvg = svg.cloneNode(true) as SVGSVGElement;
      clonedSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      clonedSvg.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
      clonedSvg.setAttribute('viewBox', `${exportX} ${exportY} ${exportW} ${exportH}`);
      clonedSvg.setAttribute('width', `${Math.ceil(exportW)}`);
      clonedSvg.setAttribute('height', `${Math.ceil(exportH)}`);
      clonedSvg.setAttribute('overflow', 'visible');

      const serializer = new XMLSerializer();
      const svgString = serializer.serializeToString(clonedSvg);

      const dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.floor(exportW * dpr));
      canvas.height = Math.max(1, Math.floor(exportH * dpr));
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.imageSmoothingEnabled = true;

      ctx.fillStyle = isDarkMode ? '#111827' : '#ffffff';
      ctx.fillRect(0, 0, exportW, exportH);

      const img = new Image();
      img.crossOrigin = 'anonymous';

      const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);

      img.onload = () => {
        try {
          ctx.drawImage(img, 0, 0, exportW, exportH);
          const dataUrl = canvas.toDataURL('image/png');
          const link = document.createElement('a');
          link.href = dataUrl;
          const base = (config.title || 'chart').toLowerCase().replace(/[^a-z0-9\-]+/g, '-');
          link.download = `${base || 'chart'}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } finally {
          URL.revokeObjectURL(url);
        }
      };
      img.onerror = () => {
        try {
          URL.revokeObjectURL(url);
        } catch {}
      };

      img.src = url;
    } catch (e) {
      // Silently fail to avoid breaking UI
      console.error('Chart share failed:', e);
    }
  }, [config.title, isDarkMode]);

  if (!config || !config.data || config.data.length === 0) {
    return (
      <div className="flex flex-col space-y-2 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 p-6 text-center">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200">No data available</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          The AI attempted to render a chart, but no data points were provided.
        </p>
      </div>
    );
  }

  const renderChart = () => {
    switch (config.type) {
      case "line":
      case "area":
      case "stacked-area": {
        const useCartesian = !!config.cartesianPlane;
        const dataForLine = useMemo(() => {
          if (!useCartesian) return config.data;
          const copy = [...config.data];
          copy.sort((a, b) => Number(a[config.xKey] ?? 0) - Number(b[config.xKey] ?? 0));
          return copy;
        }, [useCartesian, config.data, config.xKey]);
        return (
          <LineChart data={dataForLine} margin={commonMargins}>
            <CartesianGrid strokeDasharray="4 8" stroke={isDarkMode ? gridStrokeDarker : gridStrokeLight} />
            {useCartesian ? (
              <XAxis
                type="number"
                dataKey={config.xKey}
                domain={["auto", "auto"]}
                stroke={axisColor}
                tickLine={false}
                axisLine={{ stroke: axisColor }}
                label={{ value: xAxisLabel, position: "insideBottom", offset: 0, fill: axisColor }}
              />
            ) : (
              <XAxis
                dataKey={config.xKey}
                stroke={axisColor}
                tickLine={false}
                axisLine={{ stroke: axisColor }}
                tickMargin={6}
                label={{ value: xAxisLabel, position: "insideBottom", offset: 0, fill: axisColor }}
              />
            )}
            <YAxis
              stroke={axisColor}
              tickFormatter={(value) => formatter(value)}
              tickLine={false}
              axisLine={{ stroke: axisColor }}
              tickMargin={6}
              domain={["auto", "auto"]}
              label={{ value: yAxisLabel, angle: -90, position: "insideLeft", offset: 0, fill: axisColor }}
            />
            {config.showTooltip !== false && (
              <Tooltip contentStyle={tooltipContentStyle} labelStyle={tooltipLabelStyle} formatter={(value: any, name: string) => [formatter(Number(value)), name]} />
            )}
            {config.showLegend !== false && (
              <Legend verticalAlign="bottom" wrapperStyle={{ bottom: 0 }} />
            )}
            {useCartesian && (config.showZeroAxes ?? true) && (
              <>
                <ReferenceLine x={0} stroke={isDarkMode ? "#4b5563" : "#9ca3af"} strokeWidth={1} />
                <ReferenceLine y={0} stroke={isDarkMode ? "#4b5563" : "#9ca3af"} strokeWidth={1} />
              </>
            )}
            {renderLineSeries(
              config.yKeys,
              config.colors,
              config.type !== "line",
              config.type === "stacked-area" || config.stacked
            )}
          </LineChart>
        );
      }
      case "bar":
      case "monochrome-bar": {
        const colors = config.type === "monochrome-bar" ? [pickColor(0, config.colors)] : config.colors;
        return (
          <BarChart data={config.data} margin={commonMargins}>
            <CartesianGrid strokeDasharray="4 8" stroke={isDarkMode ? gridStrokeDark : gridStrokeLight} />
            <XAxis
              dataKey={config.xKey}
              stroke={axisColor}
              tickLine={false}
              axisLine={{ stroke: axisColor }}
              tickMargin={6}
              label={{ value: xAxisLabel, position: "insideBottom", offset: 0, fill: axisColor }}
            />
            <YAxis
              stroke={axisColor}
              tickFormatter={(value) => formatter(value)}
              tickLine={false}
              axisLine={{ stroke: axisColor }}
              tickMargin={6}
              label={{ value: yAxisLabel, angle: -90, position: "insideLeft", offset: 0, fill: axisColor }}
            />
            {config.showTooltip !== false && (
              <Tooltip contentStyle={tooltipContentStyle} labelStyle={tooltipLabelStyle} formatter={(value: any, name: string) => [formatter(Number(value)), name]} />
            )}
            {config.showLegend !== false && (
              <Legend verticalAlign="bottom" wrapperStyle={{ bottom: 0 }} />
            )}
            {renderBarSeries(config.yKeys, colors, config.stacked)}
          </BarChart>
        );
      }
      case "pie": {
        return (
          <PieChart>
            {config.showTooltip !== false && <Tooltip formatter={(value: any, name: string) => [formatter(Number(value)), name]} />}
            {renderPieSeries(config)}
            {config.showLegend !== false && (
              <Legend verticalAlign="bottom" wrapperStyle={{ bottom: 0 }} />
            )}
          </PieChart>
        );
      }
      case "donut": {
        return (
          <PieChart>
            {config.showTooltip !== false && <Tooltip formatter={(value: any, name: string) => [formatter(Number(value)), name]} />}
            {renderPieSeries(config, "45%", "70%")}
            {config.showLegend !== false && (
              <Legend verticalAlign="bottom" wrapperStyle={{ bottom: 0 }} />
            )}
          </PieChart>
        );
      }
      case "scatter":
      case "bubble": {
        return (
          <ScatterChart margin={commonMargins}>
            <CartesianGrid strokeDasharray="4 8" stroke={isDarkMode ? gridStrokeDark : gridStrokeLight} />
            <XAxis
              type="number"
              dataKey={config.xKey}
              stroke={axisColor}
              tickLine={false}
              axisLine={{ stroke: axisColor }}
              tickMargin={6}
              label={{ value: xAxisLabel, position: "insideBottom", offset: 0, fill: axisColor }}
              name={config.xKey}
            />
            <YAxis
              type="number"
              dataKey={config.yKeys[0].key}
              stroke={axisColor}
              tickLine={false}
              axisLine={{ stroke: axisColor }}
              tickMargin={6}
              label={{ value: yAxisLabel, angle: -90, position: "insideLeft", offset: 0, fill: axisColor }}
              name={config.yKeys[0].name || config.yKeys[0].key}
            />
            {(config.cartesianPlane ?? true) && (config.showZeroAxes ?? true) && (
              <>
                <ReferenceLine x={0} stroke={isDarkMode ? "#4b5563" : "#9ca3af"} strokeWidth={1} />
                <ReferenceLine y={0} stroke={isDarkMode ? "#4b5563" : "#9ca3af"} strokeWidth={1} />
              </>
            )}
            {config.type === "bubble" && config.scatterSizeKey && (
              <ZAxis dataKey={config.scatterSizeKey} range={[80, 400]} />
            )}
            {config.showTooltip !== false && (
              <Tooltip cursor={{ strokeDasharray: "4 4" }} contentStyle={tooltipContentStyle} />
            )}
            {config.showLegend !== false && (
              <Legend verticalAlign="bottom" wrapperStyle={{ bottom: 0 }} />
            )}
            <Scatter data={config.data} fill={pickColor(0, config.colors)} />
          </ScatterChart>
        );
      }
      case "radar": {
        return (
          <RadarChart data={config.data} outerRadius="70%">
            <PolarGrid stroke={isDarkMode ? gridStrokeDark : gridStrokeLight} />
            <PolarAngleAxis dataKey={config.radarAngleKey || config.xKey} stroke={axisColor} />
            <PolarRadiusAxis stroke={axisColor} />
            {config.showLegend !== false && (
              <Legend verticalAlign="bottom" wrapperStyle={{ bottom: 0 }} />
            )}
            {config.yKeys.map((serie, index) => {
              const color = pickColor(index, config.colors);
              return (
                <Radar
                  key={serie.key}
                  name={serie.name || serie.key}
                  dataKey={serie.key}
                  stroke={color}
                  fill={color}
                  fillOpacity={0.35}
                />
              );
            })}
          </RadarChart>
        );
      }
      case "mixed": {
        return (
          <ComposedChart data={config.data} margin={commonMargins}>
            <CartesianGrid strokeDasharray="4 8" stroke={isDarkMode ? gridStrokeDark : gridStrokeLight} />
            <XAxis
              dataKey={config.xKey}
              stroke={axisColor}
              tickLine={false}
              axisLine={{ stroke: axisColor }}
              label={{ value: xAxisLabel, position: "insideBottom", offset: 0, fill: axisColor }}
            />
            <YAxis
              stroke={axisColor}
              tickFormatter={(value) => formatter(value)}
              tickLine={false}
              axisLine={{ stroke: axisColor }}
              label={{ value: yAxisLabel, angle: -90, position: "insideLeft", offset: 0, fill: axisColor }}
            />
            {config.showTooltip !== false && (
              <Tooltip contentStyle={tooltipContentStyle} labelStyle={tooltipLabelStyle} formatter={(value: any, name: string) => [formatter(Number(value)), name]} />
            )}
            {config.showLegend !== false && (
              <Legend verticalAlign="bottom" wrapperStyle={{ bottom: 0 }} />
            )}
            {config.mixedConfig?.map((serie, index) => {
              const color = serie.color || pickColor(index, config.colors);
              if (serie.type === "bar") {
                return (
                  <Bar key={serie.key} dataKey={serie.key} name={serie.name || serie.key} fill={color} radius={[6, 6, 0, 0]} />
                );
              }
              if (serie.type === "area") {
                return (
                  <Area
                    key={serie.key}
                    type="monotone"
                    dataKey={serie.key}
                    name={serie.name || serie.key}
                    fill={color}
                    stroke={color}
                    fillOpacity={0.35}
                    strokeWidth={serie.strokeWidth || 2}
                  />
                );
              }
              return (
                <Line
                  key={serie.key}
                  type="monotone"
                  dataKey={serie.key}
                  name={serie.name || serie.key}
                  stroke={color}
                  strokeWidth={serie.strokeWidth || 2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 6 }}
                />
              );
            })}
          </ComposedChart>
        );
      }
      case "implicit": {
        // Prepare implicit function f(x,y)=0 (memoized to avoid recompute on theme change)
        const f = useMemo(() => config.implicitFunction || buildImplicitFn(config.implicitEquation), [config.implicitFunction, config.implicitEquation]);
        if (!f) {
          return (
            <div className="flex h-full items-center justify-center text-sm text-gray-500 dark:text-gray-400">
              Unable to parse implicit equation. Provide `implicitFunction` or a valid `implicitEquation`.
            </div>
          );
        }
        const xRange: [number, number] = config.xRange || [-2.5, 2.5];
        const yRange: [number, number] = config.yRange || [-2.5, 2.5];
        const cells = Math.min(600, Math.max(50, config.resolution || 220));

        // SVG dims with responsive viewBox
        const W = 1000;
        const H = 600;
        const PAD = 36;

        // Build path (cache heavy marching squares across theme toggles)
        const d = useMemo(
          () =>
            marchingSquaresPath(
              f,
              xRange[0],
              xRange[1],
              yRange[0],
              yRange[1],
              cells,
              W,
              H,
              PAD
            ),
          [f, xRange[0], xRange[1], yRange[0], yRange[1], cells]
        );

        // Axes in pixel coords
        const xt = (x: number) => PAD + ((x - xRange[0]) / (xRange[1] - xRange[0])) * (W - 2 * PAD);
        const yt = (y: number) => H - PAD - ((y - yRange[0]) / (yRange[1] - yRange[0])) * (H - 2 * PAD);
        const zeroXIn = 0 >= xRange[0] && 0 <= xRange[1];
        const zeroYIn = 0 >= yRange[0] && 0 <= yRange[1];

        const curveColor = pickColor(0, config.colors);

        return (
          <svg className="recharts-surface" width="100%" height="100%" viewBox={`0 0 ${W} ${H}`}>
            <rect x="0" y="0" width={W} height={H} fill="transparent" />
            {/* Grid */}
            <g stroke={isDarkMode ? gridStrokeDark : gridStrokeLight} strokeDasharray="4 8">
              {Array.from({ length: 8 }, (_, i) => {
                const t = (i + 1) / 9;
                const y = PAD + t * (H - 2 * PAD);
                const x = PAD + t * (W - 2 * PAD);
                return (
                  <g key={i}>
                    <line x1={PAD} y1={y} x2={W - PAD} y2={y} />
                    <line x1={x} y1={PAD} x2={x} y2={H - PAD} />
                  </g>
                );
              })}
            </g>
            {/* Axes */}
            {zeroYIn && (
              <line x1={PAD} y1={yt(0)} x2={W - PAD} y2={yt(0)} stroke={isDarkMode ? "#4b5563" : "#9ca3af"} strokeWidth={1} />
            )}
            {zeroXIn && (
              <line x1={xt(0)} y1={PAD} x2={xt(0)} y2={H - PAD} stroke={isDarkMode ? "#4b5563" : "#9ca3af"} strokeWidth={1} />
            )}
            {/* Axis Labels */}
            <text
              x={W / 2}
              y={H - PAD + 22}
              textAnchor="middle"
              fill={axisColor}
              fontSize={12}
            >
              {xAxisLabel || "x"}
            </text>
            <text
              x={PAD - 28}
              y={H / 2}
              textAnchor="middle"
              fill={axisColor}
              fontSize={12}
              transform={`rotate(-90 ${PAD - 28}, ${H / 2})`}
            >
              {yAxisLabel || "y"}
            </text>
            {/* Curve */}
            <path d={d} fill="none" stroke={curveColor} strokeWidth={2} />
          </svg>
        );
      }
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col space-y-4">
      {(config.title || config.description) && (
        <div>
          {config.title && (
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {config.title}
            </h3>
          )}
          {config.description && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {config.description}
            </p>
          )}
        </div>
      )}
      <div
        ref={containerRef}
        className="relative w-full h-[360px] sm:bg-white sm:dark:bg-[#2c2c2c] bg-transparent rounded-2xl sm:border border-gray-200 border-0 dark:border-[#2c2c2c] sm:py-4 sm:pr-4 sm:pl-2 px-0 py-2"
      >
        <button
          type="button"
          onClick={handleShare}
          title="Download chart"
          className="absolute z-10 top-0 right-0 inline-flex items-center justify-center h-8 w-8 rounded-xl bg-white/90 dark:bg-[#2a2a2a]
           hover:bg-white dark:hover:bg-[#2c2c2c]  border-gray-200/80 dark:border-[#3a3a3a] shadow-sm backdrop-blur text-gray-700 dark:text-gray-200"
        >
          <Download className="w-4 h-4" />
        </button>
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export const ChartRenderer = React.memo(ChartRendererComp);
